import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ChannelModalLayout from '../Layouts/ChannelModalLayout';
import { saveLanguageToLocalStorage, saveInterfaceLanguageToLocalStorage } from '../../utils/themeStorage';

const LanguageFilter = ({ 
    selectedLanguage, 
    onLanguageChange, 
    className = "",
    size = "sm", // sm, md, lg
    fullWidth = false // для полной ширины выпадающего списка
}) => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Список языков с флагами и кодами
    const languages = [
        { code: 'ru', name: 'Русский', flag: '🇷🇺' },
        { code: 'en', name: 'English', flag: '🇺🇸' }
    ];

    const sizeClasses = {
        sm: "px-2 py-1 text-sm",
        md: "px-3 py-2 text-base", 
        lg: "px-4 py-3 text-lg"
    };

    const selectedLanguageData = languages.find(lang => lang.code === selectedLanguage) || languages[0];

    // Обработка клика вне выпадающего меню
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Обработка клика вне мобильного модального окна
    useEffect(() => {
        const handleMobileClickOutside = (event) => {
            // Проверяем, что клик не по кнопке открытия модального окна
            if (buttonRef.current && !buttonRef.current.contains(event.target)) {
                // Проверяем, что клик не по самому модальному окну
                const modalElement = document.querySelector('[data-modal="language-filter"]');
                if (modalElement && !modalElement.contains(event.target)) {
                    setIsMobileModalOpen(false);
                }
            }
        };

        if (isMobileModalOpen) {
            document.addEventListener('mousedown', handleMobileClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleMobileClickOutside);
        };
    }, [isMobileModalOpen]);

    // Обработка выбора языка
    const handleLanguageSelect = (languageCode) => {
        onLanguageChange(languageCode);
        // Сохраняем выбор языка в куки
        saveLanguageToLocalStorage(languageCode);
        // Также изменяем язык интерфейса, если выбран конкретный язык
        if (languageCode === 'ru' || languageCode === 'en') {
            i18n.changeLanguage(languageCode);
            saveInterfaceLanguageToLocalStorage(languageCode);
        }
        setIsOpen(false);
        setIsMobileModalOpen(false);
    };

    // Обработка клика по кнопке
    const handleButtonClick = () => {
        if (window.innerWidth <= 768) {
            // Мобильная версия - открываем модальное окно
            setIsMobileModalOpen(true);
        } else {
            // Десктопная версия - открываем выпадающее меню
            setIsOpen(!isOpen);
        }
    };

    const buttonClasses = `bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${sizeClasses[size]} ${className}`;

    return (
        <div className="relative">
            {/* Кнопка выбора языка */}
            <button
                ref={buttonRef}
                onClick={handleButtonClick}
                className={buttonClasses}
            >
                <span className="flex items-center gap-2">
                    <span>{selectedLanguageData.name}</span>
                </span>
                <ChevronDownIcon className="w-4 h-4 ml-2" />
            </button>

            {/* Десктопное выпадающее меню */}
            {isOpen && (
                <div 
                    ref={dropdownRef}
                    className={`absolute ${fullWidth ? 'left-0 right-0' : 'right-0'} top-full py-2 mt-1 ${fullWidth ? 'w-full' : 'w-auto min-w-[150px]'} origin-top-right bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-900 rounded shadow-lg z-[9999]`}
                >
                    {/* Список языков */}
                    <div>
                        {languages.map((language) => (
                            <button
                                key={language.code}
                                onClick={() => handleLanguageSelect(language.code)}
                                className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-3 ${
                                    selectedLanguage === language.code 
                                        ? 'bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
                                        : 'text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                <span className="text-lg">{language.flag}</span>
                                <span className="text-sm">{language.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Мобильное модальное окно */}
            <div data-modal="language-filter">
                <ChannelModalLayout
                    isOpen={isMobileModalOpen}
                    onClose={() => {
                        setIsMobileModalOpen(false);
                    }}
                    title="Выбор языка"
                >
                <div className="space-y-2">
                    {languages.map((language) => (
                        <button
                            key={language.code}
                            onClick={() => handleLanguageSelect(language.code)}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center gap-3 rounded-lg ${
                                selectedLanguage === language.code 
                                    ? 'bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
                                    : 'text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            <span className="text-2xl">{language.flag}</span>
                            <span className="text-lg">{language.name}</span>
                        </button>
                    ))}
                </div>
                </ChannelModalLayout>
            </div>
        </div>
    );
};

export default LanguageFilter;